/**
 * Session Chat Page - FSM-Driven Chat Interface
 * 
 * Uses finite state machine pattern for explicit state management.
 * State-driven UI rendering makes the chat flow clear and testable.
 */

import { useEffect, useRef } from "react";

import { Loader2, GitBranch, Bookmark } from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { ActiveMemoriesIndicator } from "@/components/chat/ActiveMemoriesIndicator";
import { BranchIndicator } from "@/components/chat/BranchIndicator";
import { BranchList } from "@/components/chat/BranchList";
import { ChatDialogsContainer } from "@/components/chat/ChatDialogsContainer";
import { ChatInput } from "@/components/chat/ChatInput";
import { MessageList } from "@/components/chat/MessageList";
import { StaleBranchDetector } from "@/components/chat/StaleBranchDetector";
import { AppBreadcrumb } from "@/components/navigation/AppBreadcrumb";
import SentryErrorBoundary from "@/components/SentryErrorBoundary";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useAuth } from "@/contexts/AuthContext";
import { useChatState, chatKeys } from "@/hooks/useChatState";
import { useKeyboardNavigation } from "@/hooks/useKeyboardNavigation";
import { useUserPreferences } from "@/hooks/useUserPreferences";
import { ROUTES } from "@/lib/routes";

const SessionChat = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  
  useKeyboardNavigation({
    enabled: false, // Disabled to avoid conflicts with text editing
  });
  
  const { preferences } = useUserPreferences();
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const {
    session,
    messages,
    branches,
    bookmarkedMessages,
    isLoading,
    uiState,
    messageRefs,
    sendMessage,
    saveEditedMessage,
    regenerateResponse,
    toggleBookmark,
    createBranch,
    handleBranchFromMessage,
    handleSwitchBranch,
    handleArchiveBranch,
    handleUnarchiveBranch,
    handleMergeBranch,
    handleAnalyzeMerge,
    handleInitiateMerge,
    injectFramework,
    setInput,
    setEditingContent,
    toggleBookmarkPanel,
    toggleFrameworkWidget,
    toggleBranchList,
    setBranchDialogOpen,
    setMergeDialogOpen,
    startEditingMessage,
    cancelEditing,
    startRegenerateResponse,
    cancelRegenerating,
    jumpToMessage,
    isTitleGenerating,
  } = useChatState({ sessionId: sessionId! });

  const {
    input,
    sending,
    editingMessageId,
    editingContent,
    regeneratingMessageId,
    showRegenerateDialog,
    showBookmarkPanel,
    branchDialogOpen,
    branchingFromMessage,
    branchListOpen,
    mergeDialogOpen,
    selectedMergeBranch,
    showFrameworkWidget,
  } = uiState;

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages.length]);

  // Redirect if not authenticated (but wait for auth to load)
  if (!authLoading && !user) {
    navigate("/auth");
    return null;
  }

  /**
   * Render loading state
   */
  const renderLoadingState = () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
        <p className="text-muted-foreground">Loading conversation...</p>
      </div>
    </div>
  );

  /**
   * Render error state
   */
  const renderErrorState = () => (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center">
          <div className="text-destructive text-3xl">⚠</div>
        </div>
        <div>
          <h2 className="text-xl font-semibold">Error Loading Chat</h2>
          <p className="text-muted-foreground mt-2">
            Could not load session data. Please try again later.
          </p>
        </div>
        <Button onClick={() => navigate(ROUTES.DASHBOARD)}>
          Back to Dashboard
        </Button>
      </div>
    </div>
  );

  /**
   * Render main chat interface
   */
  const renderChatInterface = () => (
    <div className="min-h-screen bg-background flex flex-col">
      <AppBreadcrumb />

      {/* Header with branches and bookmarks */}
      <div className="border-b border-border bg-background">
        <div className="container mx-auto px-4 py-3 flex justify-between items-center">
          <div className="flex items-center gap-2">
          </div>
          
          <div className="flex gap-2">
            {branches.length > 0 && (
              <Sheet open={branchListOpen} onOpenChange={toggleBranchList}>
                <SheetTrigger asChild>
                  <Button variant="outline" size="sm">
                    <GitBranch className="h-4 w-4 mr-2" />
                    Branches ({branches.filter(b => !b.archived).length})
                  </Button>
                </SheetTrigger>
                <SheetContent className="w-[400px] sm:w-[540px] overflow-y-auto">
                  <SheetHeader>
                    <SheetTitle>Branch Management</SheetTitle>
                    <SheetDescription>
                      View and manage conversation branches. Switch between different exploration paths or archive inactive branches.
                    </SheetDescription>
                  </SheetHeader>
                  <div className="mt-6 space-y-4">
                    <StaleBranchDetector
                      projectId={session?.project_id || ""}
                      daysThreshold={7}
                      onBranchesArchived={() => queryClient.invalidateQueries({ queryKey: chatKeys.branches(sessionId!) })}
                    />
                    <BranchList
                      currentSessionId={sessionId!}
                      branches={branches}
                      onSwitchBranch={handleSwitchBranch}
                      onArchiveBranch={handleArchiveBranch}
                      onUnarchiveBranch={handleUnarchiveBranch}
                      onMergeBranch={handleMergeBranch}
                    />
                  </div>
                </SheetContent>
              </Sheet>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleBookmarkPanel()}
            >
              <Bookmark className="h-4 w-4 mr-2" />
              Bookmarks ({bookmarkedMessages.size})
            </Button>
          </div>
        </div>
      </div>

      {/* Main content area */}
      <main className="flex-1 container mx-auto px-4 py-6 max-w-4xl flex flex-col">
        {/* Session title and branch indicator */}
        <div className="mb-4">
          <div className="flex items-center gap-2 mb-4">
            <h1 className="text-2xl font-semibold">{session?.name}</h1>
            {isTitleGenerating && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span>Generating title...</span>
              </div>
            )}
            {session && (
              <BranchIndicator
                currentSession={session}
                branches={branches}
                onSwitchBranch={handleSwitchBranch}
              />
            )}
          </div>
          {session?.project_id && (
            <ActiveMemoriesIndicator projectId={session.project_id} />
          )}
        </div>

        {/* Messages list */}
        <div className="flex-1 overflow-y-auto mb-4 space-y-4">
          <MessageList
            messages={messages}
            bookmarkedMessages={bookmarkedMessages}
            uiState={uiState}
            messageRefs={messageRefs}
            chatCollapseEnabled={preferences.chat_collapse_enabled}
            chatCollapsedHeight={preferences.chat_collapsed_height}
            onToggleBookmark={toggleBookmark}
            onBranchFromMessage={handleBranchFromMessage}
            onStartEditing={startEditingMessage}
            onStartRegenerating={startRegenerateResponse}
            onSaveEdit={saveEditedMessage}
            onCancelEdit={cancelEditing}
            onEditingContentChange={setEditingContent}
          />
          <div ref={messagesEndRef} />
        </div>

        {/* Chat input */}
        <ChatInput
          sessionId={sessionId!}
          input={input}
          sending={sending}
          showFrameworkWidget={showFrameworkWidget}
          recentMessages={messages}
          onInputChange={setInput}
          onSendMessage={sendMessage}
          onToggleFrameworkWidget={toggleFrameworkWidget}
          onSelectFramework={injectFramework}
          disabled={sending || isLoading}
        />
      </main>

      {/* All dialogs */}
      <ChatDialogsContainer
        sessionId={sessionId!}
        showRegenerateDialog={showRegenerateDialog}
        onRegenerateConfirm={regenerateResponse}
        onRegenerateCancel={cancelRegenerating}
        showBookmarkPanel={showBookmarkPanel}
        onBookmarkPanelChange={toggleBookmarkPanel}
        onJumpToMessage={jumpToMessage}
        branchDialogOpen={branchDialogOpen}
        branchingFromMessage={branchingFromMessage}
        onBranchDialogChange={setBranchDialogOpen}
        onCreateBranch={createBranch}
        mergeDialogOpen={mergeDialogOpen}
        selectedMergeBranch={selectedMergeBranch}
        currentSession={session}
        onMergeDialogChange={setMergeDialogOpen}
        onInitiateMerge={(strategy, resolutions, newSessionName) => handleInitiateMerge({ strategy, resolutions, newSessionName })}
        onAnalyzeMerge={handleAnalyzeMerge}
      />
    </div>
  );

  /**
   * Main render based on FSM state
   */
  const renderContent = () => {
    if (isLoading) {
      return renderLoadingState();
    }

    if (!session && !isLoading) {
      return renderErrorState();
    }

    return renderChatInterface();
  };

  return (
    <SentryErrorBoundary
      context={{
        component: "SessionChat",
        userId: user?.id,
        additionalData: {
          sessionId,
          messageCount: messages.length,
          isBranch: session?.is_branch,
          hasParent: !!session?.parent_session_id,
        },
      }}
    >
      {renderContent()}
    </SentryErrorBoundary>
  );
};

export default SessionChat;
